/**
 * CaptureModeToggle - Two-button segmented control for selecting photo vs video capture.
 * Used in PracticeSettingsDrawer and RollSettingsDrawer to give users a discoverable
 * alternative to the long-press gesture on FloatingCaptureButton.
 * @module CaptureModeToggle
 */

import { Camera, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Capture mode value */
export type CaptureMode = 'photo' | 'video';

/**
 * Props for CaptureModeToggle component.
 */
export interface CaptureModeToggleProps {
  /** Currently selected capture mode */
  value: CaptureMode;
  /** Callback when capture mode is changed by the user */
  onChange: (mode: CaptureMode) => void;
  /** Whether the toggle is disabled (e.g. GM-locked proof mode) */
  disabled?: boolean;
  /** Optional reason text shown below the toggle when disabled (e.g. "GM requires a photo") */
  lockReason?: string;
}

/**
 * Segmented Photo / Video toggle.
 * Active option uses the accent variant; inactive uses secondary-action.
 * When disabled, both buttons are inert and the optional lockReason is shown
 * as a small caption beneath the segmented control.
 */
export function CaptureModeToggle({
  value,
  onChange,
  disabled = false,
  lockReason
}: CaptureModeToggleProps) {
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Capture mode">
        <Button
          type="button"
          variant={value === 'photo' ? 'accent' : 'secondary-action'}
          className={cn('gap-2', value === 'photo' && 'ring-2 ring-ring')}
          aria-pressed={value === 'photo'}
          aria-label="Capture mode: Photo"
          disabled={disabled}
          onClick={() => onChange('photo')}
        >
          <Camera className="h-4 w-4" />
          Photo
        </Button>
        <Button
          type="button"
          variant={value === 'video' ? 'accent' : 'secondary-action'}
          className={cn('gap-2', value === 'video' && 'ring-2 ring-ring')}
          aria-pressed={value === 'video'}
          aria-label="Capture mode: Video"
          disabled={disabled}
          onClick={() => onChange('video')}
        >
          <Video className="h-4 w-4" />
          Video
        </Button>
      </div>
      {disabled && lockReason && (
        <p className="text-xs text-muted-foreground">{lockReason}</p>
      )}
    </div>
  );
}
